import React, { useEffect } from 'react';
import {
  Mic,
  MicMute,
  CameraVideo,
  Display,
  X,
  CircleFill,
} from 'react-bootstrap-icons';
import clsx from 'clsx';
import useLiveSession from '../hooks/useLiveSession';
import LiveSelfView from './LiveSelfView';

const STATUS_LABEL = {
  idle: 'Ready',
  connecting: 'Connecting…',
  starting: 'Starting session…',
  live: 'Live',
  error: 'Error',
};

export default function LivePanel({ open, onClose, onSessionSaved }) {
  const {
    status,
    transcript,
    error,
    micMuted,
    videoMode,
    videoStream,
    lastFrameAt,
    startSession,
    endSession,
    toggleMic,
    changeVideoMode,
    isActive,
  } = useLiveSession({
    onSessionEnd: () => {
      if (onSessionSaved) onSessionSaved();
      onClose();
    },
  });

  useEffect(() => {
    if (open && status === 'idle') {
      startSession();
    }
  }, [open, status, startSession]);

  if (!open) return null;

  return (
    
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-label="Live voice session"
      >
        <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border-subtle bg-surface shadow-composer">
          <header className="flex items-center justify-between border-b border-border-subtle/40 px-4 py-3">
            <div className="flex items-center gap-2">
              {status === 'live' && (
                <CircleFill size={10} className="animate-pulse text-red-500" />
              )}
              <h2 className="text-sm font-semibold text-text-primary">
                Live with SchoolCracked
              </h2>
              <span className="text-xs text-text-muted">
                {STATUS_LABEL[status] || status}
              </span>
            </div>
            <button
              type="button"
              onClick={endSession}
              className="rounded-lg p-2 text-text-muted hover:bg-surface-hover hover:text-text-primary"
              aria-label="End live session"
            >
              <X size={18} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {error && (
              <div className="mb-3 rounded-lg border border-red-900/50 bg-[#5c1a1a] px-3 py-2 text-sm text-[#ffb4b4]">
                {error}
              </div>
            )}

            {transcript.length === 0 && status === 'live' && (
              <p className="text-center text-sm text-text-muted">
                Speak naturally — I&apos;ll guide you with questions and hints.
              </p>
            )}

            <div className="space-y-3">
              {transcript.map((turn, i) => (
                <div
                  key={`${turn.role}-${i}`}
                  className={clsx(
                    'rounded-xl px-3 py-2 text-sm leading-relaxed',
                    turn.role === 'questioner'
                      ? 'ml-8 bg-surface-elevated text-text-primary'
                      : 'mr-8 bg-accent/10 text-text-primary'
                  )}
                >
                  <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                    {turn.role === 'questioner' ? 'You' : 'SchoolCracked'}
                  </span>
                  {turn.message}
                </div>
              ))}
            </div>
          </div>

          <footer className="border-t border-border-subtle/40 px-4 py-3">
            <div className="flex items-center justify-center gap-2">
              <ControlButton
                active={!micMuted}
                onClick={toggleMic}
                label={micMuted ? 'Unmute' : 'Mute'}
                disabled={!isActive}
              >
                {micMuted ? <MicMute size={18} /> : <Mic size={18} />}
              </ControlButton>

              <ControlButton
                active={videoMode === 'camera'}
                onClick={() =>
                  changeVideoMode(videoMode === 'camera' ? 'none' : 'camera')
                }
                label="Camera"
                disabled={!isActive}
              >
                <CameraVideo size={18} />
              </ControlButton>

              <ControlButton
                active={videoMode === 'screen'}
                onClick={() =>
                  changeVideoMode(videoMode === 'screen' ? 'none' : 'screen')
                }
                label="Screen"
                disabled={!isActive}
              >
                <Display size={18} />
              </ControlButton>
            </div>

            <button
              type="button"
              onClick={endSession}
              className="mt-3 w-full rounded-xl bg-danger py-2.5 text-sm font-semibold text-white hover:bg-red-700"
            >
              End session
            </button>

            <p className="mt-2 text-center text-[10px] text-text-muted">
              {videoStream
                ? 'Transcript saved when you end · Sending 1 frame per second'
                : 'Transcript saved when you end · Audio only by default'}
            </p>
          </footer>
        </div>

        <LiveSelfView
          stream={videoStream}
          mode={videoMode}
          lastFrameAt={lastFrameAt}
        />
      </div>
    
  );
}

function ControlButton({ children, onClick, label, active, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={clsx(
        'flex h-11 w-11 items-center justify-center rounded-full border transition',
        active
          ? 'border-accent bg-accent/20 text-accent'
          : 'border-border-subtle text-text-muted hover:bg-surface-hover hover:text-text-primary',
        disabled && 'cursor-not-allowed opacity-40'
      )}
    >
      {children}
    </button>
  );
}

